import { useQuery } from '@tanstack/react-query';
import { USD_DECIMALS } from '@ui/constants/numbersDecimals';
import { useDataChainId } from '@ui/hooks/datachain';
import { usePriceMapLatest } from '@ui/hooks/price/usePriceLatest';
import { getGenesisAirdrop } from '@ui/services/contract/contract-instances/getGenesisAirdrop';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { Address, parseUnits } from 'viem';
import { usePublicClient } from 'wagmi';

export type PositionBasicPoints = {
  openPoints: bigint;
  closePoints: bigint;
  holdingSeconds: bigint;
  holdingBoostP: bigint;
  teamBoostP: bigint;
};

const REFRESH_BUCKET_MS = 10000; // points are not time-critical; refresh at most once per minute

export const useGenesisAirdropBatchPositionBasicPoints = (
  positions: Position[] | undefined,
) => {
  const chainId = useDataChainId();
  const client = usePublicClient({
    chainId,
  });
  const { data: priceMap } = usePriceMapLatest();

  return useQuery({
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    queryKey: [
      'useGenesisAirdropBatchPositionBasicPoints',
      chainId,
      client?.uid,
      positions?.map((p) => p.id),
      positions?.length,
    ],
    queryFn: async (): Promise<Record<Address, PositionBasicPoints>> => {
      if (!client || !positions || positions.length === 0) return {};

      const tradeHashes: Address[] = [];
      const marketPrices: bigint[] = [];
      positions.forEach((p) => {
        const price = priceMap?.[p.pair.baseSymbol];
        if (!price) return;
        tradeHashes.push(p.id);
        marketPrices.push(parseUnits(price.toString(), USD_DECIMALS));
      });

      if (tradeHashes.length === 0) return {};

      const contract = getGenesisAirdrop({
        chainId,
        publicClient: client,
      });

      const res = await contract.read.batchCalcPositionBasicPoints([
        tradeHashes,
        marketPrices,
      ]);

      const map: Record<Address, PositionBasicPoints> = {} as any;
      res.forEach((r, i) => {
        map[tradeHashes[i]] = {
          openPoints: r.openPoints,
          closePoints: r.closePoints,
          holdingSeconds: r.holdingSeconds,
          holdingBoostP: r.holdingBoostP === 0n ? 10000n : r.holdingBoostP,
          teamBoostP: r.teamBoostP === 0n ? 10000n : r.teamBoostP,
        };
      });

      return map;
    },
    enabled: !!client && !!positions && positions.length > 0,
    // staleTime: REFRESH_BUCKET_MS,
    refetchInterval: REFRESH_BUCKET_MS,
  });
};
